import React, { useEffect, useMemo, useState } from 'react'
import { useDispatch } from 'react-redux'
import { skipToken } from '@reduxjs/toolkit/query'
import { MapComponent } from '../UI/Map/MapComponent'
import { setGPSVehiclesDetails, setMapViewVehicle, setMarkers } from '../redux/MapDataSlice'
import { useFetchSIDTokenPublicQuery, useFetchSIDTokenQuery, useGetGPSVehicleDetailsQuery } from '../redux/network/Wialon'
import type { LinkInformationData } from '../redux/network/linkInformation'


type Props = {
  linkInfo: LinkInformationData
}

/**
 * Full-screen live map + Wialon polling — mirrors VehicleMonitoring GPS flow
 * (SID token, search_items mask =${GPSVehicleCode}, Redux MapData updates).
 */
const SECURITY_KEY = "OzLDoosvT6hRcAAMLQw4HihRA4kdF1ARd6LrUrheNL79lKZad6bqMQHXIjLMgCyw"
const LiveJobTrackingView: React.FC<Props> = ({ linkInfo }) => {
  const dispatch = useDispatch()
  const [selectedFocusId, setSelectedFocusId] = useState<number>(linkInfo.gpsVehicleCode)
  const [selectedMarkerId, setSelectedMarkerId] = useState<number>(0)

  const [isCollapsed, setIsCollapsed] = useState(false)

  const itemIdString = useMemo(
    () => `=${linkInfo.gpsVehicleCode}`,
    [linkInfo.gpsVehicleCode]
  )

  const routeLocations = useMemo(
    () =>
      linkInfo.locations.map((l) => ({
        lat: l.lat,
        lng: l.lng,
        name: l.name,
      })),
    [linkInfo.locations]
  )

  useEffect(() => {
    dispatch(setMapViewVehicle([linkInfo.gpsVehicleCode]))
  }, [dispatch, linkInfo.gpsVehicleCode])

  const { data: sid, refetch: refetchSID } = useFetchSIDTokenPublicQuery(SECURITY_KEY)


  const {
    data: gpsVehicleDetails,
    error: gpsVehicleDetailsError,
    refetch: refetchGPSVehicleDetails,
  } = useGetGPSVehicleDetailsQuery(
    sid ? { itemIdsArray: itemIdString as unknown as string[], sID: sid } : skipToken,
    {
      refetchOnMountOrArgChange: true,
      pollingInterval: 6000,
      skip: !sid || !itemIdString,
    }
  )

  useEffect(() => {
    let intervalId: NodeJS.Timeout | null = null
    if (sid) {
      intervalId = setInterval(() => {
        refetchGPSVehicleDetails()
      }, 6000)

      if (!itemIdString || itemIdString.length === 0) {
        dispatch(setGPSVehiclesDetails([]))
        dispatch(setMarkers([]))
      } else if (gpsVehicleDetails) {
        dispatch(setGPSVehiclesDetails(gpsVehicleDetails))
        dispatch(setMarkers(gpsVehicleDetails))
      }

      if (gpsVehicleDetailsError) {
        setTimeout(() => {
          refetchSID()
        }, 3000)
      }
    }

    return () => {
      if (intervalId) {
        clearInterval(intervalId)
      }
    }
  }, [
    sid,
    itemIdString,
    gpsVehicleDetails,
    gpsVehicleDetailsError,
    refetchGPSVehicleDetails,
    refetchSID,
    dispatch,
  ])

  return (
    <div
      className='position-fixed top-0 start-0 w-100 h-100 overflow-hidden bg-body'
      style={{ zIndex: 1 }}
    >


      <div
        className='position-absolute top-0 start-0 p-3'
        style={{ zIndex: 20, maxWidth: 'min(360px, calc(100vw - 24px))' }}
      >
        <div className='bg-white rounded-3 shadow-sm border p-3 text-start job-card'
          style={{
            top: '12px',
            left: '12px',
            zIndex: 9999,
            width: '340px',
            maxHeight: 'calc(100vh - 24px)',
            overflowY: 'auto'
          }}
        >

          {/* 🟦 HEADER */}
          <div className='d-flex justify-content-between align-items-start mb-2'
            style={{ cursor: 'pointer' }}
            onClick={() => setIsCollapsed(prev => !prev)}
          >

            {/* LEFT SIDE */}
            <div>
              <div className='fw-bold text-dark'>
                Job Details - {linkInfo.requestId}
              </div>

              <small className='text-muted'>
                Live Tracking
              </small>
            </div>

            {/* RIGHT SIDE */}
            <div className='text-end'>

              <div className='text-muted' style={{ cursor: 'pointer', fontSize: '16px' }}>
                {isCollapsed ? '▼' : '▲'}
              </div>

              {/* ACTIVE BADGE */}
              <div className='mb-1'>
                <span className='badge bg-success'>
                  Active
                </span>
              </div>


            </div>

          </div>

          {!isCollapsed && (
            <>

              <hr className='my-2' />

              {/* 🟩 KEY INFO */}
              <div className='row g-2'>

                {linkInfo.transporterName && (
                  <div className='col-6'>
                    <small className='text-muted'>Transporter</small>
                    <div className='fw-semibold text-dark'>
                      {/*linkInfo.transporterName*/}N/A
                    </div>
                  </div>
                )}

                {linkInfo.vehicleRegistrationNo && (
                  <div className='col-6'>
                    <small className='text-muted'>Vehicle</small>
                    <div className='fw-semibold text-dark'>
                      {linkInfo.vehicleRegistrationNo}
                    </div>
                  </div>
                )}

                {linkInfo.driverName && (
                  <div className='col-6'>
                    <small className='text-muted'>Driver</small>
                    <div className='fw-semibold text-dark'>
                      {linkInfo.driverName}
                    </div>
                  </div>
                )}

                {linkInfo.driverContact && (
                  <div className='col-6'>
                    <small className='text-muted'>Contact</small>
                    <div className='fw-semibold text-dark'>
                      {linkInfo.driverContact}
                    </div>
                  </div>
                )}

              </div>

              <hr className='my-2' />

              {/* 🟨 ROUTE SUMMARY */}
              <div className='mb-2'>
                <small className='text-muted'>Route</small>

                <div className='d-flex align-items-center gap-2 mt-1 flex-wrap'>
                  <span className='badge bg-success'>Pickup</span>

                  <span className='text-muted'>→</span>

                  <span className='badge bg-primary'>
                    {routeLocations.length - 2 > 0
                      ? `${routeLocations.length - 2} Stops`
                      : 'Direct'}
                  </span>

                  <span className='text-muted'>→</span>

                  <span className='badge bg-danger'>Delivery</span>
                </div>
              </div>

              <hr className='my-2' />

              {/* 🟥 LIVE INFO (placeholder-ready for GPS updates) 
        <div className='row g-2'>

          <div className='col-4 text-center'>
            <small className='text-muted'>Speed</small>
            <div className='fw-bold text-primary'>-- km/h</div>
          </div>

          <div className='col-4 text-center'>
            <small className='text-muted'>Last Update</small>
            <div className='fw-semibold'>--</div>
          </div>

          <div className='col-4 text-center'>
            <small className='text-muted'>Status</small>
            <div className='fw-semibold text-success'>Live</div>
          </div>

        </div>


          {/* 🟪 LOCATION LIST */}
              <div>
                <small className='text-muted'>Locations</small>

                <div
                  style={{
                    marginTop: '6px',
                    maxHeight: routeLocations.length > 5 ? '120px' : 'auto',
                    overflowY: routeLocations.length > 5 ? 'auto' : 'visible',
                    paddingRight: '4px',
                  }}
                >
                  {routeLocations?.length > 0 ? (
                    routeLocations.map((loc, index) => {
                      const isFirst = index === 0
                      const isLast = index === routeLocations.length - 1

                      const label = isFirst ? 'P' : isLast ? 'D' : index

                      return (
                        <div
                          key={index}
                          className='d-flex align-items-center gap-2 py-1'
                          style={{
                            borderBottom: '1px solid #f1f1f1',
                            fontSize: '12px',
                          }}
                        >
                          <span
                            style={{
                              minWidth: '22px',
                              height: '22px',
                              borderRadius: '40px',
                              background: isFirst
                                ? '#22c55e'
                                : isLast
                                  ? '#ef4444'
                                  : '#1d4ed8',
                              color: '#fff',
                              fontSize: '11px',
                              fontWeight: 'bold',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                            }}
                          >
                            {label}
                          </span>

                          <span className='text-dark'>
                            {loc.name || `Location ${index + 1}`}
                          </span>
                        </div>
                      )
                    })
                  ) : (
                    <span className='text-muted'>No locations available</span>
                  )}
                </div>
              </div>

            </>
          )}



        </div>
      </div>


      <div className='w-100' style={{ height: '100vh' }}>
        <MapComponent
          selectedFocusId={selectedFocusId}
          selectedMarkerId={selectedMarkerId}
          setSelectedMarkerId={setSelectedMarkerId}
          routeLocations={routeLocations.length > 0 ? routeLocations : undefined}
          mapContainerHeight='100vh'
          mapFullBleed
        />
      </div>
    </div>
  )
}

export { LiveJobTrackingView }
